import * as React from 'react';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { IUserGroupBatchImportResult, userGroupBatchImport } from '../../../../api/actions/user-group/user-group-batch-import';
import { Button } from '../../../../components/shared/button/button';
import { Popup } from '../../../../components/shared/popup/popup';
import { MultiError } from '../../../../types/multi-error';
import { IUserGroupImportScanResult, scanUserGroupsCsv, toUserGroupImportApiRows } from './user-groups-import-scan';

const MAX_LISTED_ITEMS = 10;

interface IProps {
    onImportComplete?: () => void,
}

export function UserGroupsImportButton(props: IProps) {

    const fileInput = useRef<HTMLInputElement>(null);
    const [fileName, setFileName] = useState<string>();
    const [scanResult, setScanResult] = useState<IUserGroupImportScanResult>();
    const [importResult, setImportResult] = useState<IUserGroupBatchImportResult>();
    const [importing, setImporting] = useState(false);

    const onFileSelected = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        const file = ev.target.files?.[0];
        // Reset so selecting the same file again still fires onChange.
        ev.target.value = '';
        if(!file) return;

        try {
            setImportResult(undefined);
            setScanResult(scanUserGroupsCsv(await file.text()));
            setFileName(file.name);
        }
        catch(err) {
            toast.error(`Could not read ${file.name}: ${(err as Error).message}`);
        }
    };

    const close = () => {
        if(importing) return;
        const finished = !!importResult;
        setScanResult(undefined);
        setImportResult(undefined);
        setFileName(undefined);
        if(finished) {
            props.onImportComplete?.();
        }
    };

    const confirmImport = async () => {
        if(!scanResult?.validRows.length || importing) return;

        setImporting(true);
        try {
            const result = await userGroupBatchImport(toUserGroupImportApiRows(scanResult.validRows));
            setScanResult(undefined);
            setImportResult(result);
        }
        catch(err) {
            toast.error((err as MultiError).message || 'Import failed');
        }
        finally {
            setImporting(false);
        }
    };

    return (<>
        <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={onFileSelected}
        />
        <Button className="orange" onClick={() => fileInput.current?.click()}>Import Groups</Button>

        {scanResult && (
            <Popup
                title="Import Groups"
                onClose={close}
                buttons={[
                    {
                        title: 'Cancel',
                        className: 'gray',
                        onClick: close,
                        disabled: importing,
                    },
                    {
                        title: importing ? 'Importing…' : 'Confirm Import',
                        className: 'orange',
                        onClick: confirmImport,
                        disabled: importing || !scanResult.validRows.length,
                    },
                ]}
            >
                <div>
                    <p>File: {fileName}</p>
                    <p>Entries: {scanResult.total}</p>
                    <p>Valid: {scanResult.validRows.length}</p>
                    <p>Malformed: {scanResult.malformedRows.length}</p>
                    {!!scanResult.malformedRows.length && (<>
                        <ul>
                            {scanResult.malformedRows.slice(0, MAX_LISTED_ITEMS).map(r => (
                                <li key={r.rowNumber}>Row {r.rowNumber}: {r.reasons.join('; ')}</li>
                            ))}
                        </ul>
                        {scanResult.malformedRows.length > MAX_LISTED_ITEMS && (
                            <p>…and {scanResult.malformedRows.length - MAX_LISTED_ITEMS} more</p>
                        )}
                        <p>Malformed entries will be skipped.</p>
                    </>)}
                </div>
            </Popup>
        )}

        {importResult && (
            <Popup
                title="Import Results"
                onClose={close}
                buttons={[
                    {
                        title: 'Close',
                        className: 'orange',
                        onClick: close,
                    },
                ]}
            >
                <div>
                    <p>Status: {importResult.success ? 'Success' : 'Failed'}</p>
                    <p>Created: {importResult.createdCount}</p>
                    {!!importResult.createdUids?.length && (<>
                        <p>Created UIDs:</p>
                        <ul>
                            {importResult.createdUids.slice(0, MAX_LISTED_ITEMS).map(uid => (
                                <li key={uid}>{uid}</li>
                            ))}
                        </ul>
                        {importResult.createdUids.length > MAX_LISTED_ITEMS && (
                            <p>…and {importResult.createdUids.length - MAX_LISTED_ITEMS} more</p>
                        )}
                    </>)}
                    <p>Skipped: {importResult.skipped?.length || 0}</p>
                    {!!importResult.skipped?.length && (<>
                        <ul>
                            {importResult.skipped.slice(0, MAX_LISTED_ITEMS).map((item, i) => (
                                <li key={`${item}-${i}`}>{item}</li>
                            ))}
                        </ul>
                        {importResult.skipped.length > MAX_LISTED_ITEMS && (
                            <p>…and {importResult.skipped.length - MAX_LISTED_ITEMS} more</p>
                        )}
                    </>)}
                </div>
            </Popup>
        )}
    </>);
}
